import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Link, Bell, Key, Plus, Search, 
  Shield, Trash2, Edit2, Slack, Video, Building2, Mail, Copy, Check, X, UserPlus,
  FileText, Filter, BarChart3, Crown, RefreshCw, ArrowRightLeft, AlertTriangle, Palette, CreditCard, ExternalLink,
  Monitor, Wifi, Zap, ArrowRight, Info
} from 'lucide-react';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { EmailAnalytics } from './EmailAnalytics';
import { EmailEngagementDashboard } from './EmailEngagementDashboard';
import { EmailBranding } from './EmailBranding';
import { PlanBadge, UsageBar, UpgradePrompt } from './UpgradePrompt';

const tabs = [
  { id: 'users', label: 'Users & Roles', icon: Users },
  { id: 'organization', label: 'Organization', icon: Building2 },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'email-branding', label: 'Email Branding', icon: Palette },
  { id: 'audit', label: 'Audit Logs', icon: FileText },
  { id: 'email-analytics', label: 'Email Analytics', icon: BarChart3 },
  { id: 'email-engagement', label: 'Email Engagement', icon: Zap },
  { id: 'integrations', label: 'Integrations', icon: Link },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'api', label: 'API Keys', icon: Key },
];

const integrationCards = [
  { id: 'zoom', name: 'Zoom', description: 'Auto-ingest meeting recordings when they complete', color: 'from-blue-500/20 to-blue-600/10', textColor: 'text-blue-400', type: 'webhook' },
  { id: 'teams', name: 'Microsoft Teams', description: 'Sync meeting recordings and transcripts', color: 'from-purple-500/20 to-purple-600/10', textColor: 'text-purple-400', type: 'oauth' },
  { id: 'google', name: 'Google Meet', description: 'Monitor Google Drive for Meet recordings', color: 'from-green-500/20 to-green-600/10', textColor: 'text-green-400', type: 'drive' },
  { id: 'email', name: 'Email Forwarding', description: 'Forward meeting recordings to auto-process', color: 'from-amber-500/20 to-amber-600/10', textColor: 'text-amber-400', type: 'email' },
];

const apiKeys = [
  { id: 1, name: 'Production API Key', key: 'sk_prod_****************************8a4f', created: 'Mar 15, 2026', lastUsed: '2 hours ago' },
  { id: 2, name: 'Development API Key', key: 'sk_dev_****************************3b2c', created: 'Feb 28, 2026', lastUsed: '5 days ago' },
];

const actionColors = {
  LOGIN: 'bg-blue-500/20 text-blue-400',
  ROLE_CHANGE: 'bg-purple-500/20 text-purple-400',
  MEMBER_ROLE_CHANGED: 'bg-purple-500/20 text-purple-400',
  USER_DELETE: 'bg-red-500/20 text-red-400',
  MEMBER_REMOVED: 'bg-red-500/20 text-red-400',
  INVITE_SENT: 'bg-cyan-500/20 text-cyan-400',
  INVITE_RESENT: 'bg-cyan-500/20 text-cyan-400',
  INVITE_REVOKED: 'bg-orange-500/20 text-orange-400',
  ORG_RENAMED: 'bg-indigo-500/20 text-indigo-400',
  OWNERSHIP_TRANSFERRED: 'bg-amber-500/20 text-amber-400',
  MEETING_CREATE: 'bg-green-500/20 text-green-400',
  TASK_CREATE: 'bg-emerald-500/20 text-emerald-400',
  TASK_UPDATE: 'bg-yellow-500/20 text-yellow-400',
  RISK_CREATE: 'bg-orange-500/20 text-orange-400',
  RISK_UPDATE: 'bg-amber-500/20 text-amber-400',
  EXPORT_CSV: 'bg-teal-500/20 text-teal-400',
  EXPORT_PDF: 'bg-teal-500/20 text-teal-400',
  EMAIL_SENT: 'bg-indigo-500/20 text-indigo-400',
  EMAIL_DELIVERED: 'bg-emerald-500/20 text-emerald-400',
  EMAIL_OPENED: 'bg-blue-500/20 text-blue-400',
  EMAIL_CLICKED: 'bg-violet-500/20 text-violet-400',
  EMAIL_BOUNCED: 'bg-amber-500/20 text-amber-400',
  EMAIL_DROPPED: 'bg-red-500/20 text-red-400',
  LOGIN_SUCCESS: 'bg-blue-500/20 text-blue-400',
  LOGIN_FAILURE: 'bg-red-500/20 text-red-400',
  SIGNUP: 'bg-green-500/20 text-green-400',
  PASSWORD_RESET_REQUESTED: 'bg-amber-500/20 text-amber-400',
  PASSWORD_RESET_COMPLETED: 'bg-emerald-500/20 text-emerald-400',
  ROLE_CHANGED: 'bg-purple-500/20 text-purple-400',
  INTEGRATION_CONNECTED: 'bg-green-500/20 text-green-400',
  INTEGRATION_DISCONNECTED: 'bg-orange-500/20 text-orange-400',
  INTEGRATION_CONNECT_FAILED: 'bg-red-500/20 text-red-400',
};

/* ── Confirmation Modal ─────────────────────────────────── */
const ConfirmModal = ({ open, title, message, confirmLabel, danger, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm rounded-2xl bg-[#0d1221] border border-white/10 shadow-2xl p-6" data-testid="confirm-modal">
        <div className="flex items-center gap-3 mb-4">
          {danger && <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />}
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <p className="text-sm text-gray-400 mb-6">{message}</p>
        <div className="flex items-center gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 rounded-xl bg-white/5 text-gray-300 text-sm font-medium hover:bg-white/10 transition-colors" data-testid="confirm-cancel-btn">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${danger ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`} data-testid="confirm-action-btn">{confirmLabel || 'Confirm'}</button>
        </div>
      </motion.div>
    </div>
  );
};

export const SettingsScreen = () => {
  const { notifications: appNotifications } = useData();
  const { user: currentUser, isDemo } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [roleEditId, setRoleEditId] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  const [invites, setInvites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member', name: '' });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [copiedToken, setCopiedToken] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilter, setAuditFilter] = useState({ actionType: '', userId: '' });
  const [auditActionTypes, setAuditActionTypes] = useState([]);
  const [auditUsers, setAuditUsers] = useState([]);
  const [notifPrefs, setNotifPrefs] = useState({ taskNotifications: true, riskNotifications: true, meetingNotifications: true, reportEmails: true, smartDelivery: true });
  const [reportConfig, setReportConfig] = useState({ enabled: false, frequency: 'weekly', recipients: [] });
  const [newRecipient, setNewRecipient] = useState('');
  const [testEmailStatus, setTestEmailStatus] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [reportSentStatus, setReportSentStatus] = useState(null);
  // Integration state
  const [oauthConnections, setOauthConnections] = useState({});
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState(null);
  const [integrationError, setIntegrationError] = useState('');
  const [integrationSuccess, setIntegrationSuccess] = useState('');
  // Subscription usage state
  const [planUsage, setPlanUsage] = useState(null);
  // Billing state
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  // Org management state
  const [orgEditName, setOrgEditName] = useState('');
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgSuccess, setOrgSuccess] = useState('');
  const [orgError, setOrgError] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  // Confirmation state
  const [confirm, setConfirm] = useState({ open: false, title: '', message: '', danger: false, onConfirm: null });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('d3_token')}` });
  const jsonHeaders = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

  /* ── safe fetch helper ─────────────────────── */
  const safeFetch = async (url, opts = {}) => {
    try {
      const res = await fetch(url, opts);
      let data = { detail: 'Unknown error' };
      try {
        data = await res.json();
      } catch {
        // If JSON parsing fails, try to get text
        // Note: This may fail if body was already consumed
        data = { detail: res.ok ? 'Success' : `Request failed with status ${res.status}` };
      }
      return { ok: res.ok, status: res.status, data };
    } catch (fetchErr) {
      return { ok: false, status: 0, data: { detail: fetchErr.message || 'Network error' } };
    }
  };

  /* ── Data fetchers ─────────────────────────── */
  const fetchUsers = useCallback(async () => { try { const { ok, data } = await safeFetch(`${API_URL}/api/auth/users`, { headers: authHeaders() }); if (ok) setUsers(data.items || data); } catch {} }, [API_URL]);
  const fetchOrgInfo = useCallback(async () => { try { const { ok, data } = await safeFetch(`${API_URL}/api/auth/organization`, { headers: authHeaders() }); if (ok) setOrgInfo(data); } catch {} }, [API_URL]);
  const fetchInvites = useCallback(async () => { try { const { ok, data } = await safeFetch(`${API_URL}/api/auth/invites`, { headers: authHeaders() }); if (ok) setInvites(data.items || data); } catch {} }, [API_URL]);
  const fetchAuditLogs = useCallback(async () => { try { const params = new URLSearchParams({ limit: '50' }); if (auditFilter.actionType) params.set('action_type', auditFilter.actionType); if (auditFilter.userId) params.set('user_id', auditFilter.userId); const { ok, data } = await safeFetch(`${API_URL}/api/audit/logs?${params}`, { headers: authHeaders() }); if (ok) { setAuditLogs(data.items || []); setAuditTotal(data.pagination?.total || 0); } } catch {} }, [API_URL, auditFilter]);
  const fetchAuditFilters = useCallback(async () => { try { const [t, u] = await Promise.all([safeFetch(`${API_URL}/api/audit/logs/action-types`, { headers: authHeaders() }), safeFetch(`${API_URL}/api/audit/logs/users`, { headers: authHeaders() })]); if (t.ok) setAuditActionTypes(t.data); if (u.ok) setAuditUsers(u.data); } catch {} }, [API_URL]);
  const fetchNotifPrefs = useCallback(async () => { try { const { ok, data } = await safeFetch(`${API_URL}/api/notifications/preferences`, { headers: authHeaders() }); if (ok) setNotifPrefs({ taskNotifications: data.taskNotifications ?? true, riskNotifications: data.riskNotifications ?? true, meetingNotifications: data.meetingNotifications ?? true, reportEmails: data.reportEmails ?? true, smartDelivery: data.smartDelivery ?? true }); } catch {} }, [API_URL]);
  const fetchReportConfig = useCallback(async () => { try { const { ok, data } = await safeFetch(`${API_URL}/api/notifications/report-config`, { headers: authHeaders() }); if (ok) setReportConfig(data); } catch {} }, [API_URL]);
  const fetchBilling = useCallback(async () => { setBillingLoading(true); try { const [subRes, txRes] = await Promise.all([safeFetch(`${API_URL}/api/stripe/subscription`, { headers: authHeaders() }), safeFetch(`${API_URL}/api/stripe/transactions`, { headers: authHeaders() })]); if (subRes.ok) setSubscription(subRes.data); if (txRes.ok) setTransactions(txRes.data.transactions || []); } catch {} setBillingLoading(false); }, [API_URL]);
  const fetchOauthConnections = useCallback(async () => { try { const { ok, data } = await safeFetch(`${API_URL}/api/integrations/connections`, { headers: authHeaders() }); if (ok) setOauthConnections(data); } catch {} }, [API_URL]);
  const fetchPlanUsage = useCallback(async () => { try { const { ok, data } = await safeFetch(`${API_URL}/api/subscription/usage`, { headers: authHeaders() }); if (ok) setPlanUsage(data); } catch {} }, [API_URL]);

  useEffect(() => { fetchUsers(); fetchOrgInfo(); fetchInvites(); fetchAuditLogs(); fetchAuditFilters(); fetchNotifPrefs(); fetchReportConfig(); fetchBilling(); fetchOauthConnections(); fetchPlanUsage(); }, [fetchUsers, fetchOrgInfo, fetchInvites, fetchAuditLogs, fetchAuditFilters, fetchNotifPrefs, fetchReportConfig, fetchBilling, fetchOauthConnections, fetchPlanUsage]);

  // Handle OAuth callback redirect params (?connected=zoom&tab=integrations)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const tab = params.get('tab');
    const error = params.get('error');
    if (tab) setActiveTab(tab);
    if (connected) {
      setActiveTab('integrations');
      const names = { zoom: 'Zoom', microsoft: 'Microsoft Teams', google: 'Google Meet' };
      setIntegrationSuccess(`${names[connected] || connected} connected successfully!`);
      fetchOauthConnections();
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setIntegrationSuccess(''), 5000);
    }
    if (error) {
      setActiveTab('integrations');
      const errorMessages = { zoom_auth_failed: 'Zoom authorization was denied or failed.', zoom_token_failed: 'Failed to complete Zoom connection. Please try again.', microsoft_auth_failed: 'Microsoft authorization failed.', google_auth_failed: 'Google authorization failed.', token_exchange_failed: 'Token exchange failed. Please try again.', invalid_state: 'Invalid session. Please try connecting again.' };
      setIntegrationError(errorMessages[error] || `Connection error: ${error}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchOauthConnections]);

  /* ── Action handlers ───────────────────────── */
  const handleRoleChange = async (userId, newRole) => {
    const { ok, data } = await safeFetch(`${API_URL}/api/auth/users/${userId}/role`, { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify({ role: newRole }) });
    if (ok) { setUsers(prev => prev.map(u => u.id === userId ? data : u)); } else { alert(data?.detail || 'Failed to update role'); }
    setRoleEditId(null);
  };

  const handleDeleteUser = (userId, userName) => {
    setConfirm({ open: true, title: 'Remove Member', message: `Are you sure you want to remove ${userName} from the organization? This action cannot be undone.`, danger: true, onConfirm: async () => {
      setConfirm(c => ({ ...c, open: false }));
      const { ok, data } = await safeFetch(`${API_URL}/api/auth/users/${userId}`, { method: 'DELETE', headers: authHeaders() });
      if (ok) { setUsers(prev => prev.filter(u => u.id !== userId)); fetchOrgInfo(); } else { alert(data?.detail || 'Failed to remove user'); }
    }});
  };

  const handleInviteUser = async () => {
    setInviteError(''); setInviteResult(null);
    if (!inviteForm.email.trim() || !inviteForm.email.includes('@')) { setInviteError('Valid email is required'); return; }
    const { ok, data } = await safeFetch(`${API_URL}/api/auth/invite`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(inviteForm) });
    if (!ok) { setInviteError(data?.detail || 'Failed to send invite'); return; }
    setInviteResult(data); fetchInvites();
  };

  const handleRevokeInvite = (inviteId, email) => {
    setConfirm({ open: true, title: 'Revoke Invitation', message: `Revoke the pending invite for ${email}?`, danger: true, onConfirm: async () => {
      setConfirm(c => ({ ...c, open: false }));
      const { ok } = await safeFetch(`${API_URL}/api/auth/invites/${inviteId}`, { method: 'DELETE', headers: authHeaders() });
      if (ok) { setInvites(prev => prev.filter(i => i.id !== inviteId)); fetchOrgInfo(); }
    }});
  };

  const handleResendInvite = async (inviteId) => {
    const { ok, data } = await safeFetch(`${API_URL}/api/auth/invites/${inviteId}/resend`, { method: 'POST', headers: authHeaders() });
    if (ok) { fetchInvites(); } else { alert(data?.detail || 'Failed to resend invite'); }
  };

  const copyInviteToken = (tokenStr) => { navigator.clipboard.writeText(tokenStr); setCopiedToken(true); setTimeout(() => setCopiedToken(false), 2000); };

  const updateNotifPref = async (key, value) => {
    const updated = { ...notifPrefs, [key]: value }; setNotifPrefs(updated);
    await safeFetch(`${API_URL}/api/notifications/preferences`, { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(updated) });
  };

  const updateReportConfig = async (updates) => {
    const updated = { ...reportConfig, ...updates }; setReportConfig(updated);
    await safeFetch(`${API_URL}/api/notifications/report-config`, { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify({ enabled: updated.enabled, frequency: updated.frequency, recipients: updated.recipients || [] }) });
  };

  const sendTestEmail = async () => { setTestEmailStatus('sending'); try { const { ok } = await safeFetch(`${API_URL}/api/notifications/send-test-email`, { method: 'POST', headers: authHeaders() }); setTestEmailStatus(ok ? 'success' : 'error'); } catch { setTestEmailStatus('error'); } setTimeout(() => setTestEmailStatus(null), 4000); };
  const sendReportNow = async () => { setSendingReport(true); setReportSentStatus(null); try { const { ok } = await safeFetch(`${API_URL}/api/notifications/send-report`, { method: 'POST', headers: authHeaders() }); setReportSentStatus(ok ? 'success' : 'error'); } catch { setReportSentStatus('error'); } setTimeout(() => setReportSentStatus(null), 5000); setSendingReport(false); };
  const addRecipient = () => { const email = newRecipient.trim().toLowerCase(); if (email && email.includes('@') && !reportConfig.recipients?.includes(email)) { updateReportConfig({ recipients: [...(reportConfig.recipients || []), email] }); setNewRecipient(''); } };
  const removeRecipient = (email) => { updateReportConfig({ recipients: (reportConfig.recipients || []).filter(r => r !== email) }); };

  /* ── Org management handlers ───────────────── */
  const handleRenameOrg = async () => {
    if (!orgEditName.trim()) return;
    setOrgSaving(true); setOrgError(''); setOrgSuccess('');
    const { ok, data } = await safeFetch(`${API_URL}/api/auth/organization/rename`, { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify({ name: orgEditName.trim() }) });
    if (ok) { setOrgSuccess('Organization renamed successfully'); setIsEditingOrg(false); fetchOrgInfo(); setTimeout(() => setOrgSuccess(''), 3000); } else { setOrgError(data?.detail || 'Failed to rename'); }
    setOrgSaving(false);
  };

  const handleTransferOwnership = () => {
    if (!transferTarget) return;
    const target = users.find(u => u.id === transferTarget);
    setConfirm({ open: true, title: 'Transfer Ownership', message: `Transfer organization ownership to ${target?.name || 'this user'}? You will remain an admin but will no longer be the owner.`, danger: true, onConfirm: async () => {
      setConfirm(c => ({ ...c, open: false }));
      const { ok, data } = await safeFetch(`${API_URL}/api/auth/organization/transfer-ownership`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ newOwnerId: transferTarget }) });
      if (ok) { setShowTransferModal(false); setTransferTarget(''); fetchOrgInfo(); fetchUsers(); } else { alert(data?.detail || 'Transfer failed'); }
    }});
  };

  const isOwner = orgInfo?.ownerId === currentUser?.id;

  /* ── Integration connect/disconnect ──────── */
  const handleConnectProvider = async (provider) => {
    setConnectingProvider(provider);
    setIntegrationError('');
    try {
      const { ok, data } = await safeFetch(`${API_URL}/api/integrations/${provider}/authorize`, { headers: authHeaders() });
      if (ok && data.authorizationUrl) {
        window.location.href = data.authorizationUrl;
        return;
      } else if (data?.detail?.error === 'plan_required') {
        setIntegrationError(data.detail);
      } else {
        setIntegrationError(data?.detail || `Failed to connect ${provider}`);
      }
    } catch (e) { setIntegrationError(`Connection failed: ${e.message || 'Unknown error'}`); }
    setConnectingProvider(null);
  };

  const handleDisconnectProvider = (provider) => {
    const names = { zoom: 'Zoom', microsoft: 'Microsoft Teams', google: 'Google Meet' };
    setConfirm({ open: true, title: `Disconnect ${names[provider] || provider}`, message: `This will remove the ${names[provider]} connection. Meeting ingestion from this provider will stop. You can reconnect at any time.`, danger: true, onConfirm: async () => {
      setConfirm(c => ({ ...c, open: false }));
      setDisconnectingProvider(provider);
      setIntegrationError('');
      try {
        const { ok, data } = await safeFetch(`${API_URL}/api/integrations/${provider}/disconnect`, { method: 'POST', headers: authHeaders() });
        if (ok) { fetchOauthConnections(); }
        else { setIntegrationError(data?.detail || 'Failed to disconnect'); }
      } catch { setIntegrationError('Disconnect failed'); }
      setDisconnectingProvider(null);
    }});
  };

  const handleBillingCheckout = async (planId, billingCycle) => {
    setCheckoutLoading(planId);
    try {
      const originUrl = window.location.origin;
      const { ok, data } = await safeFetch(`${API_URL}/api/stripe/create-checkout`, {
        method: 'POST', headers: jsonHeaders(),
        body: JSON.stringify({ plan_id: planId, billing_cycle: billingCycle, origin_url: originUrl }),
      });
      if (ok && data.url) { window.location.href = data.url; }
      else { alert(data?.detail || 'Failed to create checkout'); }
    } catch { alert('Something went wrong'); }
    setCheckoutLoading(null);
  };

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const originUrl = window.location.origin;
      const { ok, data } = await safeFetch(`${API_URL}/api/stripe/portal`, {
        method: 'POST', headers: jsonHeaders(),
        body: JSON.stringify({ origin_url: originUrl }),
      });
      if (ok && data.url) { window.location.href = data.url; }
      else { setPortalError(data?.detail || 'Could not open billing portal'); }
    } catch { setPortalError('Something went wrong'); }
    setPortalLoading(false);
  };

  /* ── Render ────────────────────────────────── */
  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50" data-testid="settings-user-search" />
              </div>
              {isAdmin && (
                <button onClick={() => { setShowInviteModal(true); setInviteResult(null); setInviteError(''); setInviteForm({ email: '', role: 'member', name: '' }); }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity ml-4" data-testid="invite-user-btn">
                  <UserPlus className="w-4 h-4" /><span className="text-sm">Invite User</span>
                </button>
              )}
            </div>

            {/* Members Table */}
            <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr className="text-left text-sm text-gray-400">
                    <th className="px-5 py-4 font-medium">User</th>
                    <th className="px-5 py-4 font-medium">Role</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 font-medium">Joined</th>
                    {isAdmin && <th className="px-5 py-4 font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => !searchQuery || u.name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase())).map((u) => {
                    const initials = u.name ? u.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';
                    const isSelf = u.id === currentUser?.id;
                    return (
                      <tr key={u.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`user-row-${u.id}`}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                              <span className="text-sm font-bold text-white">{initials}</span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium">{u.name}{isSelf ? ' (You)' : ''}</p>
                                {u.isOwner && <Crown className="w-3.5 h-3.5 text-amber-400" title="Organization Owner" />}
                              </div>
                              <p className="text-sm text-gray-400">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          {isAdmin && roleEditId === u.id ? (
                            <div className="flex items-center gap-1">
                              {['admin', 'manager', 'member'].map(role => (
                                <button key={role} onClick={() => handleRoleChange(u.id, role)}
                                  className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize transition-colors ${role === 'admin' ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' : role === 'manager' ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'} ${u.role === role ? 'ring-1 ring-white/30' : ''}`}
                                  data-testid={`role-option-${role}`}>{role}</button>
                              ))}
                              <button onClick={() => setRoleEditId(null)} className="ml-1 p-1 rounded hover:bg-white/10"><X className="w-3 h-3 text-gray-500" /></button>
                            </div>
                          ) : (
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : u.role === 'manager' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>{u.role}</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Active
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-gray-400">{u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '\u2014'}</span>
                        </td>
                        {isAdmin && (
                          <td className="px-5 py-4">
                            {!isSelf && !u.isOwner ? (
                              <div className="flex items-center gap-2">
                                <button onClick={() => setRoleEditId(roleEditId === u.id ? null : u.id)} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Change role" data-testid={`edit-role-${u.id}`}>
                                  <Edit2 className="w-4 h-4 text-gray-400" />
                                </button>
                                <button onClick={() => handleDeleteUser(u.id, u.name)} className="p-2 rounded-lg hover:bg-red-500/10 transition-colors" title="Remove member" data-testid={`delete-user-${u.id}`}>
                                  <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-400" />
                                </button>
                              </div>
                            ) : isSelf ? (
                              <span className="text-xs text-gray-600">{'\u2014'}</span>
                            ) : (
                              <span className="text-xs text-amber-400/60">Owner</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pending Invites */}
            {invites.length > 0 && (
              <div className="mt-6">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-400" />Pending Invitations
                  <span className="text-xs text-gray-500">({invites.filter(i => i.status === 'pending').length} pending)</span>
                </h3>
                <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr className="text-left text-sm text-gray-400">
                        <th className="px-5 py-3 font-medium">Email</th>
                        <th className="px-5 py-3 font-medium">Role</th>
                        <th className="px-5 py-3 font-medium">Status</th>
                        <th className="px-5 py-3 font-medium">Invited</th>
                        {isAdmin && <th className="px-5 py-3 font-medium">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {invites.map((inv) => {
                        const statusColor = inv.status === 'pending' ? 'bg-amber-500/10 text-amber-400' : inv.status === 'accepted' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400';
                        return (
                          <tr key={inv.id} className="border-t border-white/5" data-testid={`invite-row-${inv.id}`}>
                            <td className="px-5 py-3"><span className="text-white text-sm">{inv.email}</span></td>
                            <td className="px-5 py-3">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${inv.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : inv.role === 'manager' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>{inv.role}</span>
                            </td>
                            <td className="px-5 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor}`}>{inv.status}</span>
                            </td>
                            <td className="px-5 py-3">
                              <span className="text-sm text-gray-400">{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                            </td>
                            {isAdmin && (
                              <td className="px-5 py-3">
                                {(inv.status === 'pending' || inv.status === 'expired') && (
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => handleResendInvite(inv.id)} className="p-1.5 rounded-lg hover:bg-blue-500/10 transition-colors" title="Resend invite" data-testid={`resend-invite-${inv.id}`}>
                                      <RefreshCw className="w-4 h-4 text-blue-400" />
                                    </button>
                                    <button onClick={() => handleRevokeInvite(inv.id, inv.email)} className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors" title="Revoke invite" data-testid={`revoke-invite-${inv.id}`}>
                                      <X className="w-4 h-4 text-gray-400 hover:text-red-400" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 'organization':
        return (
          <div className="space-y-6" data-testid="organization-tab">
            {/* Org Profile */}
            {orgInfo && (
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      {isEditingOrg ? (
                        <div className="flex items-center gap-2">
                          <input type="text" value={orgEditName} onChange={(e) => setOrgEditName(e.target.value)} maxLength={100}
                            className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-lg font-bold focus:outline-none focus:border-blue-500/50 w-72"
                            data-testid="org-name-input" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleRenameOrg()} />
                          <button onClick={handleRenameOrg} disabled={orgSaving} className="px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 text-sm font-medium hover:bg-blue-500/30 disabled:opacity-50" data-testid="org-save-name-btn">
                            {orgSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button onClick={() => { setIsEditingOrg(false); setOrgError(''); }} className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-4 h-4 text-gray-400" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-bold text-white" data-testid="org-name">{orgInfo.name}</h2>
                          {isAdmin && (
                            <button onClick={() => { setOrgEditName(orgInfo.name); setIsEditingOrg(true); setOrgError(''); setOrgSuccess(''); }}
                              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Rename organization" data-testid="org-edit-name-btn">
                              <Edit2 className="w-4 h-4 text-gray-400" />
                            </button>
                          )}
                        </div>
                      )}
                      <p className="text-sm text-gray-400 mt-0.5">ID: {orgInfo.id}</p>
                    </div>
                  </div>
                </div>

                {orgSuccess && <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm flex items-center gap-2" data-testid="org-success"><Check className="w-4 h-4" />{orgSuccess}</div>}
                {orgError && <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="org-error">{orgError}</div>}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-sm text-gray-400 mb-1">Members</p>
                    <p className="text-2xl font-bold text-white" data-testid="org-member-count">{orgInfo.memberCount}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-sm text-gray-400 mb-1">Admins</p>
                    <p className="text-2xl font-bold text-white">{orgInfo.adminCount}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-sm text-gray-400 mb-1">Pending Invites</p>
                    <p className="text-2xl font-bold text-white">{orgInfo.pendingInvites}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-sm text-gray-400 mb-1">Created</p>
                    <p className="text-lg font-semibold text-white">{orgInfo.createdAt ? new Date(orgInfo.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '\u2014'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Ownership */}
            {orgInfo && (
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" />Organization Ownership</h3>
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{orgInfo.ownerName || 'Unknown'}</p>
                      <p className="text-sm text-gray-400">{orgInfo.ownerEmail || ''}</p>
                    </div>
                  </div>
                  {isOwner && (
                    <button onClick={() => { setShowTransferModal(true); setTransferTarget(''); }}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500/10 text-amber-400 text-sm font-medium hover:bg-amber-500/20 transition-colors border border-amber-500/20"
                      data-testid="transfer-ownership-btn">
                      <ArrowRightLeft className="w-4 h-4" />Transfer Ownership
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Data Isolation */}
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
              <h3 className="text-white font-semibold mb-2">Data Isolation</h3>
              <p className="text-sm text-gray-400 mb-4">All data in D3 Command is strictly isolated by organization. No cross-organization data access is possible.</p>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-400" />
                <span className="text-sm text-emerald-400 font-medium">Multi-tenant isolation active</span>
              </div>
            </div>
          </div>
        );

      case 'email-branding':
        return <EmailBranding />;

      case 'billing':
        return (
          <div className="space-y-6" data-testid="billing-tab">
            {/* Current Plan */}
            <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-indigo-400" />Current Plan</h3>
              {billingLoading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm"><RefreshCw className="w-4 h-4 animate-spin" />Loading...</div>
              ) : subscription?.subscription ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                    <div>
                      <p className="text-white font-bold text-lg">{subscription.subscription.plan_name}</p>
                      <p className="text-sm text-gray-400 capitalize">{subscription.subscription.billing_cycle} billing &bull; ${subscription.subscription.amount?.toFixed(2)}/{subscription.subscription.billing_cycle === 'yearly' ? 'year' : 'month'}</p>
                    </div>
                    <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-medium">Active</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <p className="text-xs text-gray-500 mb-1">Plan</p>
                      <p className="text-sm text-white font-medium">{subscription.subscription.plan_name}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <p className="text-xs text-gray-500 mb-1">Status</p>
                      <p className="text-sm text-emerald-400 font-medium capitalize">{subscription.subscription.status}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <p className="text-xs text-gray-500 mb-1">Billing Cycle</p>
                      <p className="text-sm text-white capitalize">{subscription.subscription.billing_cycle}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                      <p className="text-xs text-gray-500 mb-1">Started</p>
                      <p className="text-sm text-white">{subscription.subscription.started_at ? new Date(subscription.subscription.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</p>
                    </div>
                  </div>
                  {/* Manage Billing Button */}
                  {subscription?.hasPortalAccess && (
                    <button onClick={handleOpenPortal} disabled={portalLoading}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
                      data-testid="manage-billing-btn">
                      {portalLoading ? (
                        <><RefreshCw className="w-4 h-4 animate-spin" /> Opening portal...</>
                      ) : (
                        <><ExternalLink className="w-4 h-4" /> Manage Billing</>
                      )}
                    </button>
                  )}
                  {portalError && <p className="text-sm text-red-400">{portalError}</p>}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-400 mb-2">You're on the <span className="text-white font-medium">Free</span> plan</p>
                  <p className="text-sm text-gray-500 mb-4">Upgrade to unlock premium features</p>
                </div>
              )}
            </div>

            {/* Upgrade Options — show for free users or to change plan */}
            {(!subscription?.subscription || subscription?.plan === 'free') && (
              <div className="space-y-3">
                <h3 className="text-white font-semibold">Upgrade Your Plan</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { id: 'starter', name: 'Starter', price: 29, desc: 'Small teams', selfServe: true },
                    { id: 'growth', name: 'Growth', price: 79, desc: 'Growing teams', popular: true, selfServe: true },
                    { id: 'enterprise', name: 'Enterprise', price: null, desc: 'Full control', selfServe: false },
                  ].map(plan => (
                    <div key={plan.id} className={`p-5 rounded-2xl border transition-all ${plan.popular ? 'bg-indigo-500/5 border-indigo-500/30' : 'bg-white/5 border-white/10 hover:border-white/20'}`}>
                      <h4 className="text-white font-medium">{plan.name}</h4>
                      <p className="text-sm text-gray-500 mb-3">{plan.desc}</p>
                      {plan.selfServe ? (
                        <p className="text-2xl font-bold text-white mb-4">${plan.price}<span className="text-sm text-gray-500 font-normal">/mo</span></p>
                      ) : (
                        <p className="text-2xl font-bold text-white mb-4">Custom</p>
                      )}
                      {plan.selfServe ? (
                        <button onClick={() => handleBillingCheckout(plan.id, 'monthly')} disabled={checkoutLoading === plan.id}
                          className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors ${plan.popular ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-white/10 hover:bg-white/15 text-white'} disabled:opacity-50`}
                          data-testid={`billing-upgrade-${plan.id}`}>
                          {checkoutLoading === plan.id ? 'Processing...' : 'Subscribe'}
                        </button>
                      ) : (
                        <button onClick={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); setTimeout(() => { const el = document.querySelector('#demo'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); }}
                          className="w-full py-2.5 rounded-xl text-sm font-medium bg-white/10 hover:bg-white/15 text-white transition-colors flex items-center justify-center gap-2"
                          data-testid="billing-contact-sales">
                          Contact Sales <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Plan Usage */}
            {planUsage && (
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10" data-testid="plan-usage-section">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-3">
                  <BarChart3 className="w-4 h-4 text-indigo-400" />
                  Plan Usage
                  <PlanBadge plan={planUsage.plan} />
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <UsageBar label="Team Members" current={planUsage.usage?.teamMembers?.current || 0} limit={planUsage.usage?.teamMembers?.limit} />
                  <UsageBar label="Meetings This Month" current={planUsage.usage?.meetingsThisMonth?.current || 0} limit={planUsage.usage?.meetingsThisMonth?.limit} />
                  <UsageBar label="Storage" current={planUsage.usage?.storageGb?.current || 0} limit={planUsage.usage?.storageGb?.limit} unit=" GB" />
                </div>
                {planUsage.features && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { key: 'aiInsights', label: 'AI Insights' },
                      { key: 'riskCompliance', label: 'Risk & Compliance' },
                      { key: 'advancedAnalytics', label: 'Advanced Analytics' },
                      { key: 'auditLogs', label: 'Audit Logs' },
                    ].map(f => (
                      <div key={f.key} className={`px-3 py-2 rounded-lg text-xs font-medium ${planUsage.features[f.key] ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-white/5 text-gray-500 border border-white/5'}`}>
                        {planUsage.features[f.key] ? '\u2713' : '\u2717'} {f.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Payment History */}
            <div>
              <h3 className="text-white font-semibold mb-3">Payment History</h3>
              <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr className="text-left text-sm text-gray-400">
                      <th className="px-5 py-3 font-medium">Date</th>
                      <th className="px-5 py-3 font-medium">Plan</th>
                      <th className="px-5 py-3 font-medium">Amount</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr><td colSpan={4} className="px-5 py-8 text-center text-gray-500 text-sm">No payment history yet.</td></tr>
                    ) : transactions.map((tx) => (
                      <tr key={tx.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`tx-row-${tx.id}`}>
                        <td className="px-5 py-3"><span className="text-sm text-gray-300">{tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span></td>
                        <td className="px-5 py-3"><span className="text-sm text-white">{tx.plan_name} ({tx.billing_cycle})</span></td>
                        <td className="px-5 py-3"><span className="text-sm text-white font-medium">${tx.amount?.toFixed(2)}</span></td>
                        <td className="px-5 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tx.payment_status === 'paid' ? 'bg-emerald-500/10 text-emerald-400' : tx.payment_status === 'expired' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>
                            {tx.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'email-analytics':
        return <EmailAnalytics />;

      case 'email-engagement':
        return <EmailEngagementDashboard />;

      case 'audit':
        return (
          <div className="space-y-4" data-testid="audit-logs-tab">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2"><Filter className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-400">Filters:</span></div>
              <select value={auditFilter.actionType} onChange={(e) => setAuditFilter(prev => ({ ...prev, actionType: e.target.value }))}
                className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-blue-500/50" data-testid="audit-filter-action">
                <option value="">All Actions</option>
                {auditActionTypes.map(t => (<option key={t} value={t}>{t.replace(/_/g, ' ')}</option>))}
              </select>
              <select value={auditFilter.userId} onChange={(e) => setAuditFilter(prev => ({ ...prev, userId: e.target.value }))}
                className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-blue-500/50" data-testid="audit-filter-user">
                <option value="">All Users</option>
                {auditUsers.map(u => (<option key={u.id} value={u.id}>{u.name}</option>))}
              </select>
              <span className="text-xs text-gray-500 ml-auto">{auditTotal} total events</span>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr className="text-left text-sm text-gray-400">
                    <th className="px-5 py-3 font-medium">Timestamp</th>
                    <th className="px-5 py-3 font-medium">User</th>
                    <th className="px-5 py-3 font-medium">Action</th>
                    <th className="px-5 py-3 font-medium">Entity</th>
                    <th className="px-5 py-3 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-500 text-sm">No audit events recorded yet.</td></tr>
                  ) : auditLogs.map((log) => {
                    const color = actionColors[log.action] || 'bg-gray-500/20 text-gray-400';
                    const meta = log.metadata || {};
                    const detailStr = Object.entries(meta).filter(([, v]) => v && typeof v === 'string').map(([k, v]) => `${k}: ${v}`).join(', ');
                    return (
                      <tr key={log.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`audit-row-${log.id}`}>
                        <td className="px-5 py-3"><span className="text-sm text-gray-300">{log.timestamp ? new Date(log.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span></td>
                        <td className="px-5 py-3"><span className="text-sm text-white">{log.actorName || 'System'}</span></td>
                        <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{log.action?.replace(/_/g, ' ')}</span></td>
                        <td className="px-5 py-3"><span className="text-sm text-gray-400">{log.entityType || '\u2014'}</span></td>
                        <td className="px-5 py-3"><span className="text-xs text-gray-500 truncate block max-w-xs" title={detailStr}>{detailStr || '\u2014'}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'integrations':
        const integrationIcons = { zoom: Video, microsoft: Monitor, google: Wifi };
        const providerMeta = {
          zoom: { name: 'Zoom', description: 'Auto-ingest meeting recordings when they complete', color: 'from-blue-500/20 to-blue-600/10', textColor: 'text-blue-400' },
          microsoft: { name: 'Microsoft Teams', description: 'Sync meeting recordings and calendar events', color: 'from-purple-500/20 to-purple-600/10', textColor: 'text-purple-400' },
          google: { name: 'Google Meet', description: 'Import Google Calendar meetings and Meet recordings', color: 'from-green-500/20 to-green-600/10', textColor: 'text-green-400' },
        };
        return (
          <div className="space-y-6" data-testid="integrations-tab">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold text-lg">Meeting Integrations</h3>
                <p className="text-sm text-gray-400 mt-1">Connect your meeting platforms for automatic recording ingestion</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                <Zap className="w-3.5 h-3.5 text-indigo-400" />
                <span className="text-xs font-medium text-indigo-300">Auto-Ingest Pipeline</span>
              </div>
            </div>

            {integrationSuccess && (
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm flex items-center gap-2" data-testid="integration-success">
                <Zap className="w-4 h-4 shrink-0" />{integrationSuccess}
                <button onClick={() => setIntegrationSuccess('')} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}

            {integrationError && (
              typeof integrationError === 'object' && integrationError.error === 'plan_required' ? (
                <UpgradePrompt error={integrationError} onClose={() => setIntegrationError('')} onUpgrade={() => { setIntegrationError(''); setActiveTab('billing'); }} />
              ) : (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2" data-testid="integration-error">
                  <AlertTriangle className="w-4 h-4 shrink-0" />{typeof integrationError === 'string' ? integrationError : integrationError?.message || 'An error occurred'}
                  <button onClick={() => setIntegrationError('')} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
                </div>
              )
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {['zoom', 'microsoft', 'google'].map((providerId) => {
                const meta = providerMeta[providerId];
                const CardIcon = integrationIcons[providerId] || Link;
                const conn = oauthConnections[providerId] || {};
                const isConnected = conn.connected === true;
                const isConfigured = conn.configured !== false;
                const needsReauth = conn.needsReauth === true;
                const isConnecting = connectingProvider === providerId;
                const isDisconnecting = disconnectingProvider === providerId;
                return (
                  <div key={providerId} className={`p-5 rounded-2xl bg-gradient-to-br ${meta.color} border ${isConnected ? 'border-green-500/30' : 'border-white/10'} hover:border-white/20 transition-all`} data-testid={`integration-card-${providerId}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                          <CardIcon className={`w-6 h-6 ${meta.textColor}`} />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{meta.name}</h3>
                          <p className="text-xs text-gray-400 mt-0.5">{meta.description}</p>
                        </div>
                      </div>
                    </div>

                    {isConnected ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            {needsReauth ? (
                              <><span className="w-2 h-2 rounded-full bg-amber-400" /><span className="text-amber-400 font-medium">Needs Re-auth</span></>
                            ) : (
                              <><span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /><span className="text-green-400 font-medium">Connected</span></>
                            )}
                          </div>
                        </div>
                        {conn.accountEmail && (
                          <div className="text-xs text-gray-400 truncate" title={conn.accountEmail}>
                            {conn.accountName || conn.accountEmail}
                          </div>
                        )}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>Connected by {conn.connectedByName || 'Admin'}</span>
                          {conn.lastSync && <span>Synced {new Date(conn.lastSync).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                        </div>
                        {isAdmin && (
                          <div className="flex gap-2">
                            {needsReauth && (
                              <button onClick={() => handleConnectProvider(providerId)} disabled={isConnecting}
                                className="flex-1 py-2 rounded-xl text-xs font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors border border-amber-500/20 disabled:opacity-50"
                                data-testid={`reconnect-${providerId}-btn`}>
                                {isConnecting ? 'Reconnecting...' : 'Reconnect'}
                              </button>
                            )}
                            <button onClick={() => handleDisconnectProvider(providerId)} disabled={isDisconnecting}
                              className={`${needsReauth ? 'flex-1' : 'w-full'} py-2 rounded-xl text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors border border-red-500/20 disabled:opacity-50`}
                              data-testid={`disconnect-${providerId}-btn`}>
                              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {!isConfigured && (
                          <div className="flex items-center gap-1.5 text-xs text-amber-400/80 mb-1">
                            <Info className="w-3 h-3" />
                            <span>Credentials not configured</span>
                          </div>
                        )}
                        {isAdmin ? (
                          <button onClick={() => handleConnectProvider(providerId)} disabled={isConnecting || !isConfigured}
                            className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${isConfigured ? `bg-white/10 ${meta.textColor} hover:bg-white/15` : 'bg-white/5 text-gray-500 cursor-not-allowed'}`}
                            data-testid={`connect-${providerId}-btn`}>
                            {isConnecting ? (
                              <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Connecting...</>
                            ) : (
                              <><span>Connect {meta.name}</span><ArrowRight className="w-3.5 h-3.5" /></>
                            )}
                          </button>
                        ) : (
                          <div className="text-xs text-gray-500 text-center py-2">Admin access required to connect</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Email Ingestion Instructions */}
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10" data-testid="email-ingestion-instructions">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-white font-medium">Email Forwarding Setup</h4>
                  <p className="text-xs text-gray-400">Works with any meeting platform — no integration required</p>
                </div>
              </div>
              <div className="space-y-2 ml-13">
                {['Forward meeting recordings or transcripts to: ', 'Include the meeting title in the subject line', 'Attach audio/video files or paste transcript in the email body', 'D3 Command will automatically process and extract tasks, risks, and decisions'].map((step, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <span className="text-amber-400 font-bold mt-0.5">{i + 1}.</span>
                    <span>{i === 0 ? <>{step}<code className="px-2 py-0.5 rounded bg-white/10 text-amber-300 text-xs">meetings@d3command.com</code></> : step}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pipeline diagram */}
            <div className="p-5 rounded-2xl bg-gradient-to-r from-indigo-500/5 to-cyan-500/5 border border-white/10">
              <h4 className="text-white font-medium text-sm mb-3">How It Works</h4>
              <div className="flex items-center gap-3 flex-wrap">
                {['Recording Received', 'Audio Transcription', 'AI Analysis', 'Tasks & Risks Created'].map((step, i) => (
                  <div key={step} className="flex items-center gap-2">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                      <span className="text-xs font-medium text-gray-300">{step}</span>
                    </div>
                    {i < 3 && <ArrowRight className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6" data-testid="notifications-tab">
            <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
              <div><p className="text-white font-medium text-sm">Test Email Delivery</p><p className="text-gray-400 text-xs mt-0.5">Send a test email to verify SendGrid is configured correctly</p></div>
              <button onClick={sendTestEmail} disabled={testEmailStatus === 'sending'}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${testEmailStatus === 'success' ? 'bg-emerald-500/20 text-emerald-400' : testEmailStatus === 'error' ? 'bg-red-500/20 text-red-400' : testEmailStatus === 'sending' ? 'bg-white/10 text-gray-400 cursor-wait' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`}
                data-testid="send-test-email-btn">
                {testEmailStatus === 'sending' ? 'Sending...' : testEmailStatus === 'success' ? 'Sent!' : testEmailStatus === 'error' ? 'Failed' : 'Send Test Email'}
              </button>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-3">Email Notification Preferences</h3>
              <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
                <table className="w-full"><thead className="bg-white/5"><tr className="text-left text-sm text-gray-400"><th className="px-5 py-3 font-medium">Notification Type</th><th className="px-5 py-3 font-medium text-center">Enabled</th></tr></thead>
                  <tbody>
                    {[
                      { key: 'taskNotifications', label: 'Task Assigned / Created', desc: 'When a new task is created in your org' },
                      { key: 'riskNotifications', label: 'Risk Status Updates', desc: 'When a risk status changes' },
                      { key: 'meetingNotifications', label: 'Meeting Processed', desc: 'When AI finishes analyzing a meeting' },
                      { key: 'reportEmails', label: 'Scheduled Reports', desc: 'Weekly/monthly analytics reports' },
                      { key: 'smartDelivery', label: 'Smart Email Delivery', desc: 'Automatically reduce emails to users with low engagement scores' },
                    ].map(({ key, label, desc }) => (
                      <tr key={key} className="border-t border-white/5">
                        <td className="px-5 py-4"><p className="text-white text-sm font-medium">{label}</p><p className="text-gray-500 text-xs mt-0.5">{desc}</p></td>
                        <td className="px-5 py-4 text-center">
                          <button onClick={() => updateNotifPref(key, !notifPrefs[key])} className={`w-11 h-6 rounded-full transition-colors relative ${notifPrefs[key] ? 'bg-blue-500' : 'bg-white/10'}`} data-testid={`notif-toggle-${key}`}>
                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${notifPrefs[key] ? 'left-6' : 'left-1'}`} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-3">Scheduled Reports</h3>
              <div className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-5">
                <div className="flex items-center justify-between">
                  <div><p className="text-white font-medium text-sm">Enable Scheduled Reports</p><p className="text-gray-500 text-xs mt-0.5">Automatically generate and email analytics reports</p></div>
                  <button onClick={() => updateReportConfig({ enabled: !reportConfig.enabled })} className={`w-11 h-6 rounded-full transition-colors relative ${reportConfig.enabled ? 'bg-blue-500' : 'bg-white/10'}`} data-testid="report-enable-toggle">
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${reportConfig.enabled ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>
                {reportConfig.enabled && (
                  <>
                    <div><label className="block text-sm text-gray-400 mb-2">Frequency</label><div className="flex gap-2">
                      {['weekly', 'monthly'].map(freq => (<button key={freq} onClick={() => updateReportConfig({ frequency: freq })}
                        className={`px-5 py-2.5 rounded-xl text-sm font-medium capitalize border transition-colors ${reportConfig.frequency === freq ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-white/5 text-gray-500 border-white/10 hover:bg-white/10'}`}
                        data-testid={`report-freq-${freq}`}>{freq}</button>))}
                    </div></div>
                    <div><label className="block text-sm text-gray-400 mb-2">Additional Recipients</label>
                      <div className="flex gap-2 mb-2">
                        <input type="email" value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)} placeholder="colleague@company.com"
                          className="flex-1 h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
                          onKeyDown={(e) => e.key === 'Enter' && addRecipient()} data-testid="report-recipient-input" />
                        <button onClick={addRecipient} className="px-4 h-10 rounded-xl bg-white/10 text-white text-sm hover:bg-white/15 transition-colors" data-testid="add-recipient-btn">Add</button>
                      </div>
                      {(reportConfig.recipients || []).length > 0 && (
                        <div className="flex flex-wrap gap-2">{reportConfig.recipients.map(email => (
                          <span key={email} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300">
                            {email}<button onClick={() => removeRecipient(email)} className="hover:text-red-400 transition-colors"><X className="w-3 h-3" /></button>
                          </span>))}</div>)}
                    </div>
                    <div className="pt-2 flex items-center gap-3">
                      <button onClick={sendReportNow} disabled={sendingReport} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50" data-testid="send-report-now-btn">
                        {sendingReport ? 'Generating...' : 'Generate & Send Report Now'}
                      </button>
                      {reportSentStatus === 'success' && <span className="text-emerald-400 text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Report sent!</span>}
                      {reportSentStatus === 'error' && <span className="text-red-400 text-sm">Failed to send report</span>}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );

      case 'api':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-gray-400">Manage your API keys for external integrations</p>
              <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity"><Plus className="w-4 h-4" /><span className="text-sm">Generate New Key</span></button>
            </div>
            <div className="space-y-3">
              {apiKeys.map((apiKey) => (
                <div key={apiKey.id} className="p-5 rounded-2xl bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-medium">{apiKey.name}</h3>
                    <div className="flex items-center gap-2">
                      <button className="p-2 rounded-lg hover:bg-white/10 transition-colors"><Edit2 className="w-4 h-4 text-gray-400" /></button>
                      <button className="p-2 rounded-lg hover:bg-white/10 transition-colors"><Trash2 className="w-4 h-4 text-gray-400" /></button>
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 font-mono text-sm text-gray-400 mb-3">{apiKey.key}</div>
                  <div className="flex items-center gap-4 text-sm text-gray-400"><span>Created: {apiKey.created}</span><span>Last used: {apiKey.lastUsed}</span></div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-screen">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-white">Settings</h1><p className="text-gray-400 mt-1">Manage your account, organization, and preferences</p></div>
        {orgInfo && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
            <Building2 className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-gray-300 font-medium" data-testid="header-org-name">{orgInfo.name}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 p-1 rounded-xl bg-white/5 w-fit flex-wrap">
        {tabs.filter(t => isAdmin || !['audit', 'email-analytics', 'email-engagement', 'email-branding'].includes(t.id)).map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            data-testid={`settings-tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        {renderContent()}
      </motion.div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md rounded-2xl bg-[#0d1221] border border-white/10 shadow-2xl p-6" data-testid="invite-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-white">Invite Team Member</h3>
              <button onClick={() => setShowInviteModal(false)} className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {!inviteResult ? (
              <div className="space-y-4">
                <div><label className="block text-sm text-gray-400 mb-1.5">Email address</label>
                  <input type="email" value={inviteForm.email} onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))} placeholder="colleague@company.com"
                    className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50" data-testid="invite-email-input" /></div>
                <div><label className="block text-sm text-gray-400 mb-1.5">Name (optional)</label>
                  <input type="text" value={inviteForm.name} onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))} placeholder="John Doe"
                    className="w-full h-10 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50" data-testid="invite-name-input" /></div>
                <div><label className="block text-sm text-gray-400 mb-1.5">Role</label>
                  <div className="flex gap-2">
                    {['member', 'manager', 'admin'].map(role => (
                      <button key={role} onClick={() => setInviteForm(prev => ({ ...prev, role }))}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium capitalize transition-colors border ${inviteForm.role === role ? role === 'admin' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : role === 'manager' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-gray-500/20 text-gray-300 border-gray-500/30' : 'bg-white/5 text-gray-500 border-white/10 hover:bg-white/10'}`}
                        data-testid={`invite-role-${role}`}>{role}</button>))}
                  </div></div>
                {inviteError && <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20"><p className="text-sm text-red-400">{inviteError}</p></div>}
                <button onClick={handleInviteUser} className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity" data-testid="send-invite-btn">Send Invitation</button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2"><Check className="w-5 h-5 text-emerald-400" /><span className="text-emerald-400 font-medium">Invitation Created</span></div>
                  <p className="text-sm text-gray-300">Invite created for <strong className="text-white">{inviteResult.email}</strong> as <strong className="text-white capitalize">{inviteResult.role}</strong>.</p>
                </div>
                <div><label className="block text-sm text-gray-400 mb-1.5">Invite Token (share with the user)</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-3 rounded-xl bg-white/5 font-mono text-xs text-gray-400 truncate border border-white/10">{inviteResult.inviteToken}</div>
                    <button onClick={() => copyInviteToken(inviteResult.inviteToken)} className="p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10" data-testid="copy-invite-token">
                      {copiedToken ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-gray-400" />}
                    </button>
                  </div></div>
                <button onClick={() => setShowInviteModal(false)} className="w-full py-3 rounded-xl bg-white/10 text-white font-medium hover:bg-white/15 transition-colors">Done</button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md rounded-2xl bg-[#0d1221] border border-white/10 shadow-2xl p-6" data-testid="transfer-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-amber-400" />Transfer Ownership</h3>
              <button onClick={() => setShowTransferModal(false)} className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-400 mb-4">Select an admin to transfer ownership to. You will remain an admin but will no longer be the organization owner.</p>
            <div className="space-y-2 mb-5 max-h-60 overflow-y-auto">
              {users.filter(u => u.role === 'admin' && u.id !== currentUser?.id).length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No other admins available. Promote a user to admin first.</p>
              ) : users.filter(u => u.role === 'admin' && u.id !== currentUser?.id).map(u => (
                <button key={u.id} onClick={() => setTransferTarget(u.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${transferTarget === u.id ? 'bg-amber-500/10 border-amber-500/30' : 'bg-white/[0.02] border-white/5 hover:bg-white/5'}`}
                  data-testid={`transfer-target-${u.id}`}>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <span className="text-xs font-bold text-white">{u.name?.split(' ').map(n => n[0]).join('').toUpperCase()}</span>
                  </div>
                  <div className="text-left"><p className="text-white text-sm font-medium">{u.name}</p><p className="text-xs text-gray-400">{u.email}</p></div>
                  {transferTarget === u.id && <Check className="w-4 h-4 text-amber-400 ml-auto" />}
                </button>
              ))}
            </div>
            <button onClick={handleTransferOwnership} disabled={!transferTarget}
              className="w-full py-3 rounded-xl bg-amber-500/20 text-amber-400 font-medium hover:bg-amber-500/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="confirm-transfer-btn">Transfer Ownership</button>
          </motion.div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal open={confirm.open} title={confirm.title} message={confirm.message} danger={confirm.danger}
        confirmLabel={confirm.danger ? 'Yes, proceed' : 'Confirm'} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(c => ({ ...c, open: false }))} />
    </div>
  );
};
